import { CellCommentCard } from "@/payroll/ai-script/cell-comments/cell-comment-card";
import { ColumnHeaderMenu } from "./column-header-menu";
import { SpreadsheetColumnMenu } from "./spreadsheet-column-menu";
import { SpreadsheetRegionControl } from "./spreadsheet-region-control";
import type { SpreadsheetViewer } from "./use-spreadsheet-viewer";

interface ViewerOverlaysProps {
  viewer: SpreadsheetViewer;
  businessId: string | null;
  runId: string;
}

export const ViewerOverlays = ({
  viewer,
  businessId,
  runId,
}: ViewerOverlaysProps) => {
  const {
    gridRef,
    sheetLayout,
    mutations: {
      createSpreadsheetRegion,
      updateSpreadsheetRegion,
      deleteSpreadsheetRegion,
    },
    ui: {
      commentCard,
      setCommentCard,
      regionDialogTarget,
      setRegionDialogTarget,
      setRegionPreview,
      columnHeaderMenu,
      setColumnHeaderMenu,
      spreadsheetColumnMenu,
      setSpreadsheetColumnMenu,
    },
    sheet: {
      activeLoadedSheet,
      frozenColumn,
      canMutateActiveSheet,
      activeRegionViewStates,
    },
    comments: { areCommentsEnabled, commentIndex, commentCardAnchor },
    regions: {
      createRegion,
      updateRegion,
      deleteRegion,
      changeActiveRegionView,
    },
  } = viewer;

  return (
    <>
      {areCommentsEnabled && (
        <CellCommentCard
          businessId={businessId}
          runId={runId}
          target={commentCard}
          anchor={commentCardAnchor}
          threads={
            commentCard
              ? commentIndex.threadsAt(commentCard.row, commentCard.col)
              : []
          }
          onClose={() => setCommentCard(null)}
        />
      )}
      {activeLoadedSheet &&
        regionDialogTarget?.sheetId === activeLoadedSheet.id && (
          <SpreadsheetRegionControl
            sheet={activeLoadedSheet}
            selection={regionDialogTarget.selection}
            existingRegion={regionDialogTarget.existingRegion}
            onOpenChange={(open) => {
              if (!open) setRegionDialogTarget(null);
            }}
            isSaving={
              createSpreadsheetRegion.isPending ||
              updateSpreadsheetRegion.isPending ||
              deleteSpreadsheetRegion.isPending
            }
            onPreviewChange={setRegionPreview}
            onCreate={createRegion}
            onUpdate={updateRegion}
            onDelete={deleteRegion}
          />
        )}
      <ColumnHeaderMenu
        target={columnHeaderMenu}
        frozenColumn={frozenColumn}
        isSaving={!canMutateActiveSheet || sheetLayout.isSaving}
        onClose={() => setColumnHeaderMenu(null)}
        onFrozenColumnChange={(frozenColumn) => {
          setColumnHeaderMenu(null);
          sheetLayout.onFrozenColumnChange(frozenColumn);
        }}
      />
      <SpreadsheetColumnMenu
        target={spreadsheetColumnMenu}
        viewState={
          spreadsheetColumnMenu
            ? activeRegionViewStates[spreadsheetColumnMenu.region.id]
            : undefined
        }
        onClose={() => setSpreadsheetColumnMenu(null)}
        onFocusReturn={() => gridRef.current?.focus()}
        onChange={(state) => {
          if (spreadsheetColumnMenu) {
            changeActiveRegionView(spreadsheetColumnMenu.region.id, state);
          }
        }}
      />
    </>
  );
};
